"use client";

import { useEffect, useRef, useState } from "react";

// Plays a short muted clip once it scrolls into view and pauses it off-screen.
// The clip does not loop: it ends on its payoff frame and holds there. With
// reduced motion it skips straight to that frame instead of playing.
//
// Nothing is fetched until the clip is near the viewport: a <video poster> loads
// eagerly like an <img> without loading="lazy", which cost every visitor these
// below-the-fold posters on first paint.
export function InViewVideo({
  src,
  poster,
  className,
}: {
  src: string;
  poster: string;
  className?: string;
}) {
  const ref = useRef<HTMLVideoElement>(null);
  const [near, setNear] = useState(false);

  useEffect(() => {
    const video = ref.current;
    if (!video) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setNear(true);
          io.disconnect();
        }
      },
      { rootMargin: "600px" },
    );
    io.observe(video);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    const video = ref.current;
    if (!video || !near) return;

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      const toEnd = () => {
        video.currentTime = video.duration;
      };
      video.addEventListener("loadedmetadata", toEnd, { once: true });
      video.preload = "auto";
      video.load();
      return () => video.removeEventListener("loadedmetadata", toEnd);
    }

    const io = new IntersectionObserver(
      ([entry]) => {
        // play() on an ended clip seeks it back to the start; it holds instead.
        if (video.ended) return;
        if (entry?.isIntersecting) video.play().catch(() => {});
        else video.pause();
      },
      { threshold: 0.5 },
    );
    io.observe(video);
    return () => io.disconnect();
  }, [near]);

  return (
    <video
      ref={ref}
      src={near ? src : undefined}
      poster={near ? poster : undefined}
      muted
      playsInline
      preload="none"
      aria-hidden="true"
      className={className}
    />
  );
}
